import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../common/page_component/index.js';
import Loader from '../common/Loader.js';
import DateFilterDropdown from './DateFilterDropdown.js';
import analyticsService from '../../services/AnalyticsService';
import { Truck, TrendingUp, Package, ArrowLeft } from 'lucide-react';
import Button from '../common/Button.js';
import { useTranslation } from 'react-i18next';
import {
  AnalyticsStatCard,
  analyticsStatsGrid4Class,
  analyticsTopGridClass,
  analyticsCardGridClass,
  useAnalyticsCompanyScope,
  buildAnalyticsParams,
} from './common/index.js';
import { formatCurrencyWhole as formatCurrency } from '../../utils/formatCurrency.js';


const VehicleAnalytics = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState('current_month');
  const [customDates, setCustomDates] = useState({ from_date: '', to_date: '' });
  const {
    currentUser,
    companies,
    selectedCompany,
    handleCompanyChange,
    ready,
  } = useAnalyticsCompanyScope({ filter, customDates });

  useEffect(() => {
    if (ready) fetchData();
  }, [filter, selectedCompany, ready]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = buildAnalyticsParams({
        filter,
        customDates,
        selectedCompany,
        currentUser,
      });
      const response = await analyticsService.getVehicleAnalytics(params);
      setData(response);
    } catch (error) {
      console.error('Error fetching vehicle analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const VehicleCard = ({ vehicle, isTopPerformer }) => (
    <div className={`bg-white rounded-xl shadow-md p-3 sm:p-4 lg:p-6 hover:shadow-lg transition-shadow min-w-0 ${
      isTopPerformer ? 'border-2 border-yellow-400' : ''
    }`}>
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center">
          <div className="p-3 bg-blue-100 rounded-full mr-3">
            <Truck className="w-6 h-6 text-blue-600" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-800">{vehicle.vehicle_number}</h3>
            <p className="text-sm text-gray-600">{vehicle.vehicle_name}</p>
            <span className={`inline-block px-2 py-1 text-xs rounded-full mt-1 ${
              vehicle.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
            }`}>
              {vehicle.status}
            </span>
          </div>
        </div>
        {isTopPerformer && (
          <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs font-medium rounded-full">
            Top Performer
          </span>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4 mb-4">
        <div>
          <p className="text-xs text-gray-600">{t('pages.shipment.title')}</p>
          <p className="text-lg font-bold text-gray-800">{vehicle.performance.total_shipments}</p>
        </div>
        <div>
          <p className="text-xs text-gray-600">Revenue</p>
          <p className="text-lg font-bold text-green-600">{formatCurrency(vehicle.performance.total_revenue)}</p>
        </div>
        <div>
          <p className="text-xs text-gray-600">Avg Revenue</p>
          <p className="text-lg font-bold text-blue-600">{formatCurrency(vehicle.performance.average_revenue_per_shipment)}</p>
        </div>
        <div>
          <p className="text-xs text-gray-600">Balance</p>
          <p className="text-lg font-bold text-orange-600">{formatCurrency(vehicle.performance.balance_amount)}</p>
        </div>
      </div>

      {/* Status Breakdown */}
      {Object.keys(vehicle.shipment_status_breakdown).length > 0 && (
        <div className="border-t border-gray-200 pt-3">
          <p className="text-xs text-gray-600 mb-2">Shipment Status</p>
          <div className="flex flex-wrap gap-2">
            {Object.entries(vehicle.shipment_status_breakdown).map(([status, count]) => (
              <span key={status} className="px-2 py-1 bg-gray-100 text-xs rounded-full">
                {status}: {count}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  const StatCard = AnalyticsStatCard;

  if (loading) {
    return (
      <DashboardLayout>
        <Loader fullScreen={false} size="default" message="Loading Vehicle Analytics..." />
      </DashboardLayout>
    )
  }

  return (
    <DashboardLayout>
              <div className="max-w-7xl mx-auto">
                {/* Header with Date Filter */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-3">
                  <div>
                    <h1 className="text-2xl sm:text-xl font-semibold text-secondary">Vehicle Analytics</h1>
                    <p className="text-sm text-gray-600">Fleet performance and utilization</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <DateFilterDropdown
                      filter={filter}
                      setFilter={setFilter}
                      customDates={customDates}
                      setCustomDates={setCustomDates}
                      onApply={fetchData}
                      dateRange={data?.filter}
                      currentUser={currentUser}
                      companies={companies}
                      selectedCompany={selectedCompany}
                      onCompanyChange={handleCompanyChange}
                      showCompanyFilter
                    />
                    <Button variant="primary" icon={ArrowLeft} onClick={() => navigate('/analytics')}>Back</Button>
                  </div>
                </div>

                {/* Summary Stats */}
                {data && (
                  <>
                    <div className={`${analyticsStatsGrid4Class} mb-6`}>
                      <StatCard
                        title="Total Vehicles"
                        value={data.summary.total_vehicles}
                        subtitle="In fleet"
                        icon={Truck}
                        color="bg-blue-100 text-blue-600"
                      />
                      <StatCard
                        title="Active Vehicles"
                        value={data.summary.active_vehicles}
                        subtitle="Currently active"
                        icon={TrendingUp}
                        color="bg-emerald-100 text-emerald-600"
                      />
                      <StatCard
                        title="Vehicles with Shipments"
                        value={data.summary.vehicles_with_shipments}
                        subtitle="In this period"
                        icon={Package}
                        color="bg-purple-100 text-purple-600"
                      />
                      <StatCard
                        title="Unassigned Shipments"
                        value={data.summary.unassigned_shipments}
                        subtitle="Need assignment"
                        icon={Package}
                        color="bg-orange-100 text-orange-600"
                      />
                    </div>

                    {/* Top Performers */}
                    {data.top_performers && data.top_performers.length > 0 && (
                      <div className="mb-6">
                        <h2 className="text-xl font-bold text-gray-800 mb-4">Top Performing Vehicles</h2>
                        <div className={`${analyticsTopGridClass} mb-6`}>
                          {data.top_performers.slice(0, 3).map((vehicle) => (
                            <VehicleCard key={vehicle.vehicle_id} vehicle={vehicle} isTopPerformer={true} />
                          ))}
                        </div>
                      </div>
                    )}

                    {/* All Vehicles */}
                    <div>
                      <h2 className="text-xl font-bold text-gray-800 mb-4">All Vehicles</h2>
                      <div className={analyticsCardGridClass}>
                        {data.vehicles.map((vehicle) => (
                          <VehicleCard key={vehicle.vehicle_id} vehicle={vehicle} isTopPerformer={false} />
                        ))}
                      </div>
                    </div>

                    {data.vehicles.length === 0 && (
                      <div className="text-center py-12 bg-gray-50 rounded-xl">
                        <Truck className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                        <p className="text-gray-600">No vehicle data available for the selected period</p>
                      </div>
                    )}
                  </>
                )}
              </div>
    </DashboardLayout>
  );
};

export default VehicleAnalytics;